import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import api from '@helpers/api';
import { useCommunity } from "contexts/community";
import Parser from '@helpers/parser';
import { LoopRounded } from '@mui/icons-material';
import Popper from '@fragments/Popper';
import { Table, Text } from '@fragments';
import StatusChip from '@fragments/StatusChip';
import { getIntlLocale } from '@i18n';

export function SubscriptionTable({ userId }) {
    const { t } = useTranslation('community');

    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        pages: 0,
        records: 0,
        page: 1
    });
    const popperRef = useRef();

    const { community, loading: communityLoading } = useCommunity();

    useEffect(() => {
        if (userId && !communityLoading) {
            fetchSubscriptions();
        }
    }, [userId, communityLoading]);

    const fetchSubscriptions = async (pageNumber = 1) => {
        try {
            if (loading) return;
            setLoading(true);
            const { data: response } = await api.get(`community/subscription/subscriber/${userId}?page=${pageNumber}`);

            let list = response.data || [];
            if (list.length > 0) {
                list = list.map(subscription => {
                    subscription.community_info = community.get(subscription.community);
                    return subscription;
                });
            }

            setSubscriptions([...list]);
            setPagination({
                pages: response.pages || 0,
                records: response.records || 0,
                page: response.page || 1
            });
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            setSubscriptions([]);
            setPagination({ pages: 0, records: 0, page: 1 });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(getIntlLocale(), {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };



    const handleOpenPopper = (event, subscription) => {
        const options = [
            {
                id: 'cancel',
                label: t('tables.subscriptions.cancel.action'),
                icon: <DeleteRoundedIcon fontSize="small" />,
                variant: 'danger',
                onClick: () => {
                    // Abrir o Confirm ao invés de executar diretamente
                    window.openConfirm({
                        title: t('tables.subscriptions.cancel.action'),
                        subtitle: t('tables.subscriptions.cancel.confirmSubtitle'),
                        variant: 'danger',
                        confirmText: t('tables.subscriptions.cancel.confirmButton'),
                        cancelText: t('tables.subscriptions.cancel.cancelButton'),
                        buttons: [
                            {
                                text: t('tables.subscriptions.cancel.confirmButton'),
                                onClick: async () => {
                                    try {
                                        await api.delete(`community/${subscription.community}/subscription/${subscription.id}`);
                                        fetchSubscriptions()
                                    } catch (error) {
                                        console.error('Erro ao cancelar assinatura:', error);

                                    }
                                },
                            },
                            {
                                text: t('tables.subscriptions.cancel.cancelButton'),
                                variant: "text",
                            },
                        ]
                    });
                }
            }
        ];

        popperRef.current.open(options, 'bottom', event.currentTarget);
    };

    const columns = [
        {
            id: 'community',
            title: t('common:entities.community'),
            grow: 1,
            className: 'pl-1',
            customRender: (data) => {
                // Only the positive status gets a colored pill
                const StatusComponent = data.status === 'active' ? StatusChip.Success : StatusChip.Default;

                return (
                    <>
                        <img
                            src={data.community_info?.banner || ''}
                            className="community-row-banner"
                        />
                        <Text.span contrast={7} weight={400}>
                            {data.community_info?.name || 'N/A'}
                        </Text.span>
                        <StatusComponent text={Parser.payment.subscriptionStatus(data.status)} />
                    </>
                );
            }
        },
        {
            id: 'plan',
            title: t('common:entities.plan'),
            render: (data) => data.plan?.label || 'N/A'
        },
        {
            id: 'recurrence',
            title: t('tables.subscriptions.recurrence'),
            align: 'center',
            customRender: (data) => Parser.interval.adjective(data.interval)
        },
        {
            id: 'next_invoice',
            title: t('tables.subscriptions.nextInvoice'),
            render: (data) => formatDate(data.timestamps?.expiration)
        }
    ];

    return (
        <>
            <Text.h3 value={t('common:entities.subscriptions')} className='mb-2 mt-5' left={<LoopRounded />} />
            <Table
                id="community-user-subscriptions-table"
                columns={columns}
                data={subscriptions}
                loading={loading}
                loadingRows={4}
                enablePagination={true}
                pages={pagination.pages}
                contained={true}
                records={pagination.records}
                emptyMessage={t('tables.subscriptions.empty')}
                page={pagination.page}
                onSelectPage={fetchSubscriptions}
                actionsComponent={(subscription) => (
                    <button
                        className={`action-button ${subscription.status?.toLowerCase() === 'canceled' ? 'disabled' : ''}`}
                        onClick={(event) => handleOpenPopper(event, subscription)}
                        disabled={subscription.status?.toLowerCase() === 'canceled'}
                    >
                        <MoreVertIcon />
                    </button>
                )}
            />
            <Popper controlRef={popperRef} />
        </>
    );
}
